// Implements: F-MCK-002, F-MCK-013, F-MCK-016, F-UIX-009, F-UIX-007
/**
 * One mockup of the gallery grid (the mockup's `.mcard`). Pure presentation.
 *
 * The name is a real link to the mockup address (F-UIX-014), so middle-click, copy-link and the
 * back button all work; the whole card is its hit area (a stretched pseudo-element), while the star
 * and the menu stay separate controls above it. The thumbnail is a template (F-MCK-013, owner
 * decision 2026-10-14) drawn as inline SVG by `MockupThumbnail`. The meta line shows the scope, the
 * current version (mockup v3 P8, "v8", none before the first generation) and the day it changed.
 */

import type { ReactElement } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import type { MockupSummary } from "../../../types/api";
import { dayText } from "../../../shared/ui/dayText";
import { Badge } from "../../../shared/ui/atoms/Badge";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { MockupActions } from "./mockupActions";
import { MockupActionsMenu } from "./MockupActionsMenu";
import styles from "./MockupCard.module.css";
import { MockupThumbnail } from "./MockupThumbnail";
import { VersionBadge } from "./VersionBadge";

const SEPARATOR = "·";

export interface MockupCardProps {
  readonly mockup: MockupSummary;
  /** Where the mockup lives, in words (the scope badge). */
  readonly scopeLabel: string;
  readonly actions: MockupActions;
}

export function MockupCard({ mockup, scopeLabel, actions }: MockupCardProps): ReactElement {
  const { t } = useTranslation();

  return (
    <li className={styles.card} data-favorite={mockup.is_favorite}>
      <div className={styles.thumbnail}>
        <MockupThumbnail mockup={mockup} />
      </div>
      <div className={styles.body}>
        <div className={styles.row}>
          <Link className={styles.name} to={`/lienzo/${encodeURIComponent(mockup.id)}`}>
            {mockup.name}
          </Link>
          <MockupActionsMenu mockup={mockup} actions={actions} />
        </div>
        <div className={styles.meta}>
          <Badge tone="outline">
            <Icon name={mockup.scope_kind === "personal" ? "user" : "users"} size="sm" />
            {scopeLabel}
          </Badge>
          <VersionBadge number={mockup.current_version_number} />
          <span aria-hidden="true">{SEPARATOR}</span>
          <span className={styles.updated}>
            <span className={styles.srOnly}>{t("gallery.card.updated")}</span>
            <time dateTime={mockup.updated_at}>{dayText(mockup.updated_at)}</time>
          </span>
        </div>
      </div>
    </li>
  );
}
